import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, Plus, DollarSign, GamepadIcon, Trophy, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { AddPlayerDialog } from "./AddPlayerDialog";
import { CreateGameDialog } from "./CreateGameDialog";
import { PlayerList } from "./PlayerList";
import { GameList } from "./GameList";
import { GroupLeaderboard } from "./GroupLeaderboard";
import { Footer } from "./Footer";
import { usePWAStandalone } from "@/hooks/usePWAStandalone";
import { useAuthDebug } from "@/hooks/useAuthDebug";

interface Group {
  id: string;
  name: string;
  created_at: string;
}

interface Player {
  id: string;
  name: string;
  email?: string;
  phone?: string;
}

interface Game {
  id: string;
  title?: string;
  location?: string;
  description?: string;
  buyin_amount: number;
  chips_per_buyin: number;
  status: string;
  created_at: string;
}

export const GroupView = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [group, setGroup] = useState<Group | null>(null);
  const [players, setPlayers] = useState<Player[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showCreateGame, setShowCreateGame] = useState(false);
  const [activeTab, setActiveTab] = useState("games");
  const [currentUser, setCurrentUser] = useState<string | null>(null);
  const [userStats, setUserStats] = useState({
    totalBuyin: 0,
    netAmount: 0,
    gamesPlayed: 0,
    wins: 0,
    losses: 0
  });
  const isStandalone = usePWAStandalone();

  const { user, session, isReady } = useAuthDebug();

  useEffect(() => {
    if (!groupId) return;
    if (!isReady) return; // wait until auth state is ready
    if (!session) {
      const redirectPath = window.location.pathname + window.location.search;
      navigate(`/?login=1&redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }
    fetchGroupData();
  }, [groupId, isReady, session?.access_token]);

  const fetchGroupData = async () => {
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user?.id || null);

      // Fetch group details
      const { data: groupData, error: groupError } = await supabase
        .from('groups')
        .select('*')
        .eq('id', groupId)
        .single();

      if (groupError) throw groupError;
      setGroup(groupData);

      // Fetch players
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('group_id', groupId)
        .order('name');

      if (playersError) throw playersError;
      
      // Enhance players with profile data
      const playersWithProfiles = await Promise.all((playersData || []).map(async (player) => {
        if (player.auth_user_id) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('display_name')
            .eq('id', player.auth_user_id)
            .maybeSingle();
          return { ...player, profiles: profile };
        }
        return { ...player, profiles: null };
      }));
      
      setPlayers(playersWithProfiles);

      // Fetch games
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select('*')
        .eq('group_id', groupId)
        .order('created_at', { ascending: false });

      if (gamesError) throw gamesError;
      setGames(gamesData || []);

      // Calculate user stats for this group
      if (user?.id) {
        await calculateUserStatsForGroup(user.id, groupId!);
      }

    } catch (error) {
      console.error('Error fetching group data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch group data. Please try again.",
        variant: "destructive"
      });
      const redirectPath = window.location.pathname + window.location.search;
      navigate(`/?login=1&redirect=${encodeURIComponent(redirectPath)}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateUserStatsForGroup = async (userId: string, groupId: string) => {
    try {
      // Find user's player records in this group
      const { data: userPlayers, error: playersError } = await supabase
        .from('players')
        .select('id')
        .eq('group_id', groupId)
        .eq('auth_user_id', userId);

      if (playersError || !userPlayers || userPlayers.length === 0) {
        setUserStats({ totalBuyin: 0, netAmount: 0, gamesPlayed: 0, wins: 0, losses: 0 });
        return;
      }

      const playerIds = userPlayers.map(p => p.id);

      // Get all buyins for this user in this group
      const { data: buyins, error: buyinsError } = await supabase
        .from('buyins')
        .select('amount, game_id')
        .in('player_id', playerIds);

      if (buyinsError) throw buyinsError;

      // Get all payouts for this user in this group
      const { data: payouts, error: payoutsError } = await supabase
        .from('payouts')
        .select('net_amount, game_id')
        .in('player_id', playerIds);

      if (payoutsError) throw payoutsError;

      // Calculate stats
      const totalBuyin = (buyins || []).reduce((sum, buyin) => sum + buyin.amount, 0);
      const netAmount = (payouts || []).reduce((sum, payout) => sum + payout.net_amount, 0);
      const gamesPlayed = [...new Set((buyins || []).map(b => b.game_id))].length;
      const wins = (payouts || []).filter(p => p.net_amount > 0).length;
      const losses = (payouts || []).filter(p => p.net_amount <= 0).length;

      setUserStats({
        totalBuyin,
        netAmount,
        gamesPlayed,
        wins,
        losses
      });
    } catch (error) {
      console.error('Error calculating user stats:', error);
      setUserStats({ totalBuyin: 0, netAmount: 0, gamesPlayed: 0, wins: 0, losses: 0 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading group data...</p>
        </div>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Group not found</h2>
          <Button onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Groups
          </Button>
        </div>
      </div>
    );
  }

  const activeGames = games.filter(game => game.status === 'in_progress');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground">{group.name}</h1>
              <p className="text-sm text-muted-foreground">
                {players.length} players • {activeGames.length} active games
              </p>
            </div>
          </div>
          <div className="hidden md:flex space-x-2">
            <Button variant="outline" onClick={() => setShowAddPlayer(true)}>
              <Users className="h-4 w-4 mr-2" />
              Add Player
            </Button>
            <Button onClick={() => setShowCreateGame(true)} disabled={players.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              New Game
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Mobile Action Buttons */}
        <div className="md:hidden mb-6 flex space-x-2">
          <Button variant="outline" onClick={() => setShowAddPlayer(true)} className="flex-1">
            <Users className="h-4 w-4 mr-2" />
            Add Player
          </Button>
          <Button onClick={() => setShowCreateGame(true)} disabled={players.length === 0} className="flex-1">
            <Plus className="h-4 w-4 mr-2" />
            New Game
          </Button>
        </div>
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Buy-in</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₹{userStats.totalBuyin.toLocaleString()}</div>
              <div className={`text-xs ${userStats.netAmount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {userStats.netAmount >= 0 ? '+' : ''}₹{userStats.netAmount.toLocaleString()}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Games Played</CardTitle>
              <GamepadIcon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{userStats.gamesPlayed}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Wins & Losses</CardTitle>
              <Trophy className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{userStats.wins}</div>
              <div className="text-xs text-red-600">{userStats.losses} losses</div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="games">Games</TabsTrigger>
            <TabsTrigger value="players">Players</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
          </TabsList>

          <TabsContent value="games">
            <GameList 
              games={games} 
              players={players}
              groupId={groupId!}
              onUpdate={fetchGroupData}
              currentUserId={currentUser}
              onCreateGame={() => setShowCreateGame(true)}
            />
          </TabsContent>

          <TabsContent value="players">
            <PlayerList 
              players={players} 
              onUpdate={fetchGroupData}
              onAddPlayer={() => setShowAddPlayer(true)}
              currentUserId={currentUser}
            />
          </TabsContent>

          <TabsContent value="leaderboard">
            <GroupLeaderboard groupId={groupId!} />
          </TabsContent>
        </Tabs>

        <AddPlayerDialog
          open={showAddPlayer}
          onOpenChange={setShowAddPlayer}
          groupId={groupId!}
          onPlayerAdded={fetchGroupData}
        />

        <CreateGameDialog
          open={showCreateGame}
          onOpenChange={setShowCreateGame}
          groupId={groupId!}
          players={players}
          onGameCreated={(gameId) => {
            fetchGroupData();
            if (gameId) {
              navigate(`/game/${gameId}`);
            }
          }}
        />
      </div>
      {!isStandalone && <Footer />}
    </div>
  );
};